import { useState, FormEvent } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { resetPassword } from '../api/auth';

export default function ResetPasswordPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token') ?? '';

    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);

    if (!token) {
        return (
            <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center" bgcolor="background.default">
                <Card sx={{ width: 380, border: 'none' }}>
                    <CardContent sx={{ p: 4, textAlign: 'center' }}>
                        <LockIcon sx={{ fontSize: 48, color: 'accent.main' }} />
                        <Typography variant="h6" fontWeight={700} mt={1}>Invalid Link</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1, mb: 3 }}>
                            This password reset link is missing a token. Please request a new reset link.
                        </Typography>
                        <Button component={Link} to="/forgot-password" variant="contained" fullWidth>
                            Request New Link
                        </Button>
                    </CardContent>
                </Card>
            </Box>
        );
    }

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (password !== confirm) {
            setError('Passwords do not match.');
            return;
        }
        setLoading(true);
        setError('');
        const res = await resetPassword(token, password);
        setLoading(false);
        if (res.success) {
            setDone(true);
            setTimeout(() => navigate('/login'), 3000);
        } else {
            setError(res.message ?? 'Something went wrong. Please request a new reset link.');
        }
    };

    return (
        <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center" bgcolor="background.default">
            <Card sx={{ width: 380, border: 'none' }}>
                <CardContent sx={{ p: 4 }}>
                    <Box textAlign="center" mb={3}>
                        <LockIcon sx={{ fontSize: 48, color: 'accent.main' }} />
                        <Typography variant="h6" fontWeight={700} mt={1}>Choose a New Password</Typography>
                    </Box>

                    {done ? (
                        <Box textAlign="center">
                            <CheckCircleOutlineIcon sx={{ fontSize: 56, color: 'success.main', mb: 1 }} />
                            <Typography variant="h6" fontWeight={700} mb={1}>Password Updated!</Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                                Redirecting you to login…
                            </Typography>
                            <Button component={Link} to="/login" variant="contained" fullWidth>
                                Go to Login
                            </Button>
                        </Box>
                    ) : (
                        <>
                            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                            <Box component="form" onSubmit={handleSubmit} display="flex" flexDirection="column" gap={2}>
                                <TextField
                                    label="New Password"
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    inputProps={{ minLength: 8 }}
                                    helperText="At least 8 characters"
                                    size="small"
                                    fullWidth
                                />
                                <TextField
                                    label="Confirm Password"
                                    type="password"
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    required
                                    size="small"
                                    fullWidth
                                />
                                <Button type="submit" variant="contained" fullWidth disabled={loading}>
                                    {loading ? <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> : null}
                                    Reset Password
                                </Button>
                            </Box>
                        </>
                    )}

                    {!done && (
                        <Box mt={2}>
                            <Button component={Link} to="/login" size="small" sx={{ textTransform: 'none', color: 'accent.main' }}>
                                ← Back to login
                            </Button>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}
